import { gql, useQuery } from "@apollo/client";
import { Grid } from "@mui/material";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { ErrorBox } from "components/error";
import { Select } from "components/select";
import Roles from "components/users/Roles";
import { ScopedRole__SolutionScope } from "model/schema/globalTypes";
import { Dispatch, FormEvent } from "react";
import { notNullOrUndefined } from "utils/typescript";
import { useQueryGetAvailableRoles } from "./hooks/membersHooks";
import { Action, State } from "./hooks/useInviteSolutionMemberReducer";
import {
  QueryPossibleMembers,
  QueryPossibleMembers_solutions_Solutions_solution_Solution_access_SolutionAccessViewModel_possibleMembers_User as User
} from "./schema/QueryPossibleMembers";
import UserName from "./UserName";

const possibleMembersQuery = gql`
  query QueryPossibleMembers($solutionKey: Key!) {
    solutions {
      solution(solutionKey: $solutionKey) {
        key
        access {
          possibleMembers {
            name
            firstName
            lastName
          }
        }
      }
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      "&.MuiInput-underline:after": {
        borderBottomColor: "#3AA9AE"
      },
      "&.MuiOutlinedInput-root": {
        "&:hover fieldset": {
          borderColor: "rgba(0, 0, 0, 0.23)"
        },
        "&.Mui-focused fieldset": {
          borderColor: "#3AA9AE"
        }
      }
    },
    select: {
      margin: theme.spacing(2, 0, 1, 0)
    },
    error: {
      margin: theme.spacing(0, 0, 2, 0)
    }
  })
);

interface InviteMemberFormProps {
  solutionKey: string;
  state: State;
  dispatch: Dispatch<Action>;
  onSubmit: (event: FormEvent) => void;
}

export default function InviteMemberForm({
  solutionKey,
  state,
  dispatch,
  onSubmit
}: InviteMemberFormProps) {
  const classes = useStyles();
  const {
    data: availableRolesData,
    loading: loadingRoles,
    error: errorRoles
  } = useQueryGetAvailableRoles(solutionKey);
  const roleNames =
    availableRolesData?.solutions.solution?.access?.availableRoles ?? [];

  const {
    data: possibleMembersData,
    loading: loadingPossibleMembers,
    error: errorPossibleMembers
  } = useQuery<QueryPossibleMembers>(possibleMembersQuery, {
    variables: { solutionKey }
  });
  const possibleMembers =
    possibleMembersData?.solutions.solution?.access?.possibleMembers ?? [];

  const errors = [errorPossibleMembers, errorRoles].filter(notNullOrUndefined);
  if (errors.length > 0) {
    return (
      <Grid container className={classes.error}>
        <ErrorBox apolloError={errors} />
      </Grid>
    );
  }

  return (
    <form id="memberForm" onSubmit={onSubmit}>
      <Grid container direction="column">
        <Grid item className={classes.select}>
          <Select<User>
            loading={loadingPossibleMembers}
            options={possibleMembers}
            value={state.user}
            getOptionLabel={option =>
              `${option.firstName} ${option.lastName} (${option.name})`
            }
            renderOption={option =>
              option ? <UserName data={option} /> : null
            }
            label="User"
            id="invite-user-select"
            onChange={(_, value) => {
              dispatch({
                type: "SET_USER",
                value: value
              });
            }}
          />
        </Grid>
        <Grid item className={classes.select}>
          <Roles<ScopedRole__SolutionScope>
            roles={state.roles}
            allRoles={roleNames}
            loadingRoles={loadingRoles}
            onRoleUpdated={roles => {
              dispatch({
                type: "UPDATE_ROLES",
                value: roles
              });
            }}
            label="Roles"
            height={56}
          />
        </Grid>
      </Grid>
    </form>
  );
}
